import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Search, ChevronDown, ChevronUp, ArrowUpRight, ChevronRight, RefreshCw } from 'lucide-react';
import { api } from '../api';
import { useUser } from '../context/UserContext';
import { fmtCurrency } from '../utils';
import T from './crmTheme';
function ContactDirectory({}) {
  const { user } = useUser();
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [detail, setDetail] = useState(null);
  const [intelText, setIntelText] = useState('');
  const [addingIntel, setAddingIntel] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState('');
  const [addForm, setAddForm] = useState({ first_name: '', last_name: '', company_id: '', job_title: '', email: '', phone: '', phone_country_code: '+44', mobile: '', useful_intel: '' });
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const canEdit = ['bd', 'manager', 'director'].includes(user?.role);

  useEffect(() => { loadContacts(); }, []);

  async function loadContacts() {
    setLoading(true);
    try {
      const data = await api.crmContacts(search ? { search } : {});
      setContacts(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  useEffect(() => {
    const t = setTimeout(loadContacts, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (companySearch.length < 2) { setCompanies([]); return; }
    const t = setTimeout(async () => {
      try { setCompanies(await api.crmCompanies(companySearch)); } catch (e) { setCompanies([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [companySearch]);

  async function toggleExpand(id) {
    if (expanded === id) { setExpanded(null); setDetail(null); return; }
    setExpanded(id);
    try {
      const data = await api.crmContact(id);
      setDetail(data);
    } catch (e) { setDetail(null); }
  }

  async function addIntel(contactId) {
    if (!intelText.trim()) return;
    setAddingIntel(true);
    try {
      await api.crmAddIntel(contactId, { note_text: intelText.trim() });
      setIntelText('');
      const data = await api.crmContact(contactId);
      setDetail(data);
    } catch (e) { alert(e.message || 'Failed to add note'); }
    setAddingIntel(false);
  }

  async function saveAdd() {
    if (!addForm.first_name.trim() || !addForm.last_name.trim()) return alert('First and last name are required');
    setSavingId('new');
    try {
      await api.crmCreateContact(addForm);
      setShowAdd(false);
      setAddForm({ first_name: '', last_name: '', company_id: '', job_title: '', email: '', phone: '', phone_country_code: '+44', mobile: '', useful_intel: '' });
      loadContacts();
    } catch (e) { alert(e.message || 'Create failed'); }
    setSavingId(null);
  }

  function startEdit(d) {
    setEditingId(d.id);
    setEditForm({
      first_name: d.first_name || '',
      last_name: d.last_name || '',
      job_title: d.job_title || '',
      email: d.email || '',
      phone_country_code: d.phone_country_code || '+44',
      phone: d.phone || '',
      mobile: d.mobile || '',
      useful_intel: d.useful_intel || '',
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setEditForm({});
  }

  async function saveEdit(contactId) {
    if (!editForm.first_name.trim() || !editForm.last_name.trim()) return alert('First and last name are required');
    setSavingId(contactId);
    try {
      await api.crmUpdateContact(contactId, editForm);
      const data = await api.crmContact(contactId);
      setDetail(data);
      setEditingId(null);
      setEditForm({});
      loadContacts();
    } catch (e) { alert(e.message || 'Update failed'); }
    setSavingId(null);
  }

  const inputStyle = { width: '100%', padding: '7px 10px', borderRadius: 6, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 12, fontFamily: 'inherit', outline: 'none' };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts..." style={{ ...inputStyle, maxWidth: 320 }} />
        <span style={{ color: T.textMuted, fontSize: 12 }}>{contacts.length} contacts</span>
        {canEdit && <button onClick={() => setShowAdd(!showAdd)} style={{ marginLeft: 'auto', padding: '7px 16px', borderRadius: 6, border: 'none', background: T.teal, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>+ Add Contact</button>}
      </div>

      {showAdd && (
        <div style={{ background: T.surface, border: `1px solid ${T.teal}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: T.teal }}>New Contact</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>First Name *</label><input value={addForm.first_name} onChange={e => setAddForm(p => ({ ...p, first_name: e.target.value }))} style={inputStyle} /></div>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Last Name *</label><input value={addForm.last_name} onChange={e => setAddForm(p => ({ ...p, last_name: e.target.value }))} style={inputStyle} /></div>
            <div>
              <label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Company</label>
              <input value={companySearch} onChange={e => { setCompanySearch(e.target.value); setAddForm(p => ({ ...p, company_id: '' })); }} placeholder="Search company..." style={inputStyle} />
              {companies.length > 0 && !addForm.company_id && (
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 2, left: 0, right: 0, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, zIndex: 20, maxHeight: 120, overflowY: 'auto', boxShadow: T.shadowHover }}>
                    {companies.map(c => <div key={c.id} onClick={() => { setAddForm(p => ({ ...p, company_id: c.id })); setCompanySearch(c.name); setCompanies([]); }} style={{ padding: '6px 10px', fontSize: 11, cursor: 'pointer', borderBottom: `1px solid ${T.border}` }} onMouseEnter={e => e.currentTarget.style.background = T.surface2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>{c.name}</div>)}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Job Title</label><input value={addForm.job_title} onChange={e => setAddForm(p => ({ ...p, job_title: e.target.value }))} style={inputStyle} /></div>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Email</label><input value={addForm.email} onChange={e => setAddForm(p => ({ ...p, email: e.target.value }))} style={inputStyle} /></div>
            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Phone</label><div style={{ display: 'flex', gap: 4 }}><input value={addForm.phone_country_code} onChange={e => setAddForm(p => ({ ...p, phone_country_code: e.target.value }))} style={{ ...inputStyle, width: 60, textAlign: 'center' }} /><input value={addForm.phone} onChange={e => setAddForm(p => ({ ...p, phone: e.target.value }))} style={inputStyle} /></div></div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Context / Intel</label>
            <textarea value={addForm.useful_intel} onChange={e => setAddForm(p => ({ ...p, useful_intel: e.target.value }))} rows={2} placeholder="e.g. Bill likes skiing, prefers email contact..." style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowAdd(false)} style={{ padding: '6px 14px', borderRadius: 6, border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            <button onClick={saveAdd} disabled={savingId === 'new'} style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: T.teal, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: savingId === 'new' ? 0.6 : 1 }}>{savingId === 'new' ? 'Saving...' : 'Create Contact'}</button>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.textMuted, padding: 20 }}>Loading...</p> : (
        <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 2fr 1fr', padding: '10px 16px', borderBottom: `1px solid ${T.border}`, fontSize: 11, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            <span>Name</span><span>Company</span><span>Job Title</span><span>Email</span><span>Phone</span>
          </div>
          {contacts.length === 0 && <p style={{ color: T.textMuted, padding: 20, textAlign: 'center', fontSize: 12 }}>No contacts found</p>}
          {contacts.map(ct => (
            <div key={ct.id}>
              <div onClick={() => toggleExpand(ct.id)} style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr 2fr 1fr', padding: '10px 16px', borderBottom: `1px solid ${T.border}`, fontSize: 12, color: T.text, cursor: 'pointer', transition: 'background 0.1s', alignItems: 'center' }} onMouseEnter={e => e.currentTarget.style.background = T.surface2} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: T.tealDim, border: `1px solid ${T.tealGlow}`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: T.teal, flexShrink: 0 }}>{(ct.first_name?.[0] || '') + (ct.last_name?.[0] || '')}</div>
                  {expanded === ct.id ? '▾' : '▸'} {ct.first_name} {ct.last_name}
                </span>
                <span style={{ color: T.textMuted }}>{ct.company_name || '—'}</span>
                <span style={{ color: T.textMuted, fontSize: 11 }}>{ct.job_title || '—'}</span>
                <span style={{ color: T.textMuted, fontSize: 11 }}>{ct.email || '—'}</span>
                <span style={{ color: T.textMuted, fontFamily: "'DM Mono', monospace", fontSize: 10 }}>{ct.phone ? `${ct.phone_country_code || '+44'} ${ct.phone}` : '—'}</span>
              </div>
              {expanded === ct.id && detail && (
                <div style={{ padding: '14px 16px 14px 56px', borderBottom: `1px solid ${T.border}`, background: T.surface2 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    {/* Left: Details + Intel */}
                    <div>
                      {canEdit && editingId !== ct.id && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: 6 }}>
                          <button onClick={() => startEdit(detail)} style={{ padding: '4px 10px', borderRadius: 5, border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Edit</button>
                          <button onClick={async () => {
                            if (!window.confirm(`Archive ${detail.first_name} ${detail.last_name}? They will no longer appear in the contact list. (This is reversible via DB.)`)) return;
                            try {
                              await api.crmDeleteContact(ct.id);
                              setExpanded(null);
                              setDetail(null);
                              loadContacts();
                            } catch (e) { alert(e.message || 'Archive failed'); }
                          }} style={{ padding: '4px 10px', borderRadius: 5, border: `1px solid #b91c1c`, background: 'transparent', color: '#b91c1c', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Archive</button>
                        </div>
                      )}
                      {editingId === ct.id ? (
                        <div style={{ background: T.surface, border: `1px solid ${T.teal}`, borderRadius: 8, padding: 12, marginBottom: 10 }}>
                          <div style={{ fontWeight: 600, fontSize: 11, marginBottom: 10, color: T.teal, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Edit Contact</div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>First Name *</label><input value={editForm.first_name} onChange={e => setEditForm(p => ({ ...p, first_name: e.target.value }))} style={inputStyle} /></div>
                            <div><label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Last Name *</label><input value={editForm.last_name} onChange={e => setEditForm(p => ({ ...p, last_name: e.target.value }))} style={inputStyle} /></div>
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Job Title</label>
                            <input value={editForm.job_title} onChange={e => setEditForm(p => ({ ...p, job_title: e.target.value }))} style={inputStyle} />
                          </div>
                          <div style={{ marginBottom: 8 }}>
                            <label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Email</label>
                            <input value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} style={inputStyle} />
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                            <div>
                              <label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Phone</label>
                              <div style={{ display: 'flex', gap: 4 }}>
                                <input value={editForm.phone_country_code} onChange={e => setEditForm(p => ({ ...p, phone_country_code: e.target.value }))} style={{ ...inputStyle, width: 60, textAlign: 'center' }} />
                                <input value={editForm.phone} onChange={e => setEditForm(p => ({ ...p, phone: e.target.value }))} style={inputStyle} />
                              </div>
                            </div>
                            <div>
                              <label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Mobile</label>
                              <input value={editForm.mobile} onChange={e => setEditForm(p => ({ ...p, mobile: e.target.value }))} style={inputStyle} />
                            </div>
                          </div>
                          <div style={{ marginBottom: 10 }}>
                            <label style={{ color: T.textMuted, fontSize: 10, display: 'block', marginBottom: 3 }}>Context / Intel</label>
                            <textarea value={editForm.useful_intel} onChange={e => setEditForm(p => ({ ...p, useful_intel: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                          </div>
                          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                            <button onClick={cancelEdit} style={{ padding: '5px 12px', borderRadius: 5, border: `1px solid ${T.border}`, background: 'transparent', color: T.textMuted, fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
                            <button onClick={() => saveEdit(ct.id)} disabled={savingId === ct.id} style={{ padding: '5px 12px', borderRadius: 5, border: 'none', background: T.teal, color: '#fff', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: savingId === ct.id ? 0.6 : 1 }}>{savingId === ct.id ? 'Saving...' : 'Save'}</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {detail.useful_intel && <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 8, padding: '6px 10px', background: T.surface, borderRadius: 6, border: `1px solid ${T.border}`, fontStyle: 'italic' }}>💡 {detail.useful_intel}</div>}
                          {detail.mobile && <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 4 }}>📱 {detail.phone_country_code || '+44'} {detail.mobile}</div>}
                        </>
                      )}

                      {/* Intel Notes */}
                      <div style={{ marginTop: 10 }}>
                        <div style={{ fontWeight: 600, fontSize: 11, color: T.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Context Notes ({detail.intel_notes?.length || 0})</div>
                        {detail.intel_notes?.map(n => (
                          <div key={n.id} style={{ padding: '6px 8px', background: T.surface, borderRadius: 6, border: `1px solid ${T.border}`, marginBottom: 4, fontSize: 11 }}>
                            <div style={{ color: T.text }}>{n.note_text}</div>
                            <div style={{ color: T.textDim, fontSize: 10, marginTop: 3 }}>{n.created_by_name} — {new Date(n.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                          </div>
                        ))}
                        {canEdit && (
                          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                            <input value={intelText} onChange={e => setIntelText(e.target.value)} placeholder="Add context... e.g. Prefers morning calls" style={{ ...inputStyle, flex: 1, fontSize: 11 }} onKeyDown={e => e.key === 'Enter' && addIntel(ct.id)} />
                            <button onClick={() => addIntel(ct.id)} disabled={addingIntel || !intelText.trim()} style={{ padding: '5px 12px', borderRadius: 5, border: 'none', background: T.teal, color: '#fff', fontSize: 10, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', opacity: addingIntel ? 0.6 : 1 }}>+ Add</button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Leads + Company History */}
                    <div>
                      {detail.leads?.length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontWeight: 600, fontSize: 11, color: T.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Linked Leads ({detail.leads.length})</div>
                          {detail.leads.map(l => (
                            <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', background: T.surface, borderRadius: 5, border: `1px solid ${T.border}`, marginBottom: 3, fontSize: 11 }}>
                              <span style={{ color: T.teal, fontFamily: "'DM Mono', monospace", fontSize: 10 }}>{l.enq_ref}</span>
                              <span style={{ color: T.text, flex: 1 }}>{l.title}</span>
                              <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 3, background: T.tealDim, color: T.teal, fontWeight: 600 }}>{l.stage}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {detail.company_history?.length > 0 && (
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 11, color: T.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Company History</div>
                          {detail.company_history.map((h, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', fontSize: 11, color: T.textMuted }}>
                              <span style={{ fontWeight: h.is_current ? 600 : 400, color: h.is_current ? T.teal : T.textMuted }}>{h.company_name}</span>
                              {h.job_title && <span>— {h.job_title}</span>}
                              {h.is_current && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: T.tealDim, color: T.teal, fontWeight: 700 }}>CURRENT</span>}
                            </div>
                          ))}
                        </div>
                      )}
                      {detail.interactions?.length > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <div style={{ fontWeight: 600, fontSize: 11, color: T.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Recent Interactions ({detail.interactions.length})</div>
                          {detail.interactions.slice(0, 5).map(int => (
                            <div key={int.id} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', padding: '4px 0', borderBottom: `1px solid ${T.border}`, fontSize: 10, color: T.textMuted }}>
                              <span style={{ fontSize: 9, fontWeight: 600, padding: '1px 5px', borderRadius: 3, background: int.interaction_type === 'call' ? 'rgba(59,130,246,0.1)' : int.interaction_type === 'meeting' ? 'rgba(124,58,237,0.1)' : 'rgba(5,150,105,0.1)', color: int.interaction_type === 'call' ? '#2563eb' : int.interaction_type === 'meeting' ? '#6d28d9' : '#047857', flexShrink: 0 }}>{int.interaction_type?.toUpperCase()}</span>
                              <span style={{ flex: 1 }}>{int.notes}</span>
                              <span style={{ fontFamily: "'DM Mono', monospace", flexShrink: 0 }}>{new Date(int.interaction_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════


// ── LogOpportunityModal ───────────────────────────────────────────────────────

export default ContactDirectory;
